import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, GRADE_BADGE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Stamp } from "lucide-react";

export default function InvestorApplications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/applications")
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="page-investor-applications" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="font-overline text-accent">Pre-Approval Marketplace</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Anonymized Buyer Applications</h1>
        <p className="text-muted-foreground mt-3 max-w-3xl">
          Pre-approve a buyer for a maximum amount, rate (≤28% APR), term, and frequency. Sign the funding
          commitment and remit the <b>$2,500 escrow deposit</b>. When the borrower and a dealer source a
          vehicle within your parameters, the loan is originated and the resulting Note is assigned to you;
          your escrow deposit is released back to you at funding.
        </p>
      </div>

      {loading ? (
        <div className="text-muted-foreground font-mono-data">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-border p-12 text-center text-muted-foreground" data-testid="apps-empty">
          No published applications right now. Check back soon.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="applications-grid">
          {items.map((a) => <AppCard key={a.application_id} app={a} />)}
        </div>
      )}
    </div>
  );
}

function AppCard({ app }) {
  const p = app.derived_profile || {};
  const v = app.vehicle_preferences || {};
  return (
    <div className="border border-border bg-card hover-lift flex flex-col" data-testid={`app-card-${app.application_id}`}>
      <div className="p-5 flex flex-col gap-4 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-overline text-muted-foreground">Application · {p.state || "—"}</div>
            <h3 className="font-display text-xl leading-tight mt-1">{(v.body_type || "vehicle").toUpperCase()} up to {fmtMoney(v.max_budget)}</h3>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider ${GRADE_BADGE[p.credit_tier] || ""}`}>
              Tier {p.credit_tier || "?"}
            </span>
            {app.credit_score_band && (
              <span className="font-overline text-muted-foreground">Score {app.credit_score_band}</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-border pt-4">
          <Metric label="Income (mo)" value={p.monthly_income_range || "—"} />
          <Metric label="DTI" value={p.dti_ratio ? `${p.dti_ratio}%` : "—"} />
          <Metric label="Employment" value={(p.employment_type || "—").replace("_", " ")} />
          <Metric label="Time on Job" value={p.time_on_job_months ? `${p.time_on_job_months} mo` : "—"} />
          <Metric label="Prior Repos" value={p.prior_repos ?? "—"} />
          <Metric label="Pre-Approvals" value={app.preapproval_count || 0} />
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-border pt-3">
          <Metric label="Vehicle" value={`${v.year_min || "—"}–${v.year_max || "—"}`} />
          <Metric label="Max Miles" value={v.max_mileage ? `${(v.max_mileage / 1000).toFixed(0)}k` : "—"} />
        </div>

        <Button asChild className="rounded-none uppercase text-xs tracking-wider mt-auto" disabled={app.status !== "published" && app.status !== "preapproved"}>
          <Link to={`/applications/${app.application_id}`} data-testid={`view-app-${app.application_id}`}>
            <Stamp className="w-3.5 h-3.5 mr-1" />Pre-Approve
          </Link>
        </Button>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="font-overline text-muted-foreground">{label}</div>
      <div className="font-mono-data text-sm">{value}</div>
    </div>
  );
}
